import { Colors, Lightning } from '@lightningjs/sdk';
import Button from 'components/common/buttons/Button';
import FocusableIcon from 'components/common/FocusableIcon';
import {
  CcAudioButton,
  CommonPlaybackControls,
  DEFAULT_SEEK_RATE,
  DEFAULT_SEEK_SPEED,
  PAUSE_ICON,
  PLAY_ICON,
  SCRUB_DELAY,
  SCRUB_RATE,
  SKIP_INTERVAL,
} from './CommonPlaybackControls';
import {
  debounce,
  getImageTextureObj,
  preloadTextures,
} from 'support/generalUtils';
import { translate } from 'support/translate';
import LiveProgressBar from '../ProgressBar/LiveProgressBar';
import { LocalCoordsFromClick } from 'components/common/HoverableComponent';

interface LiveControlsTemplateSpec extends Lightning.Component.TemplateSpec {
  CcContainer: {
    CcButton: typeof FocusableIcon;
  };
  ProgressBar: typeof LiveProgressBar;
  ControlsContainer: {
    PlaybackControls: typeof CommonPlaybackControls;
    LiveButton: typeof Button;
  };
}

interface LiveControlsSignalMap extends Lightning.Component.SignalMap {
  skip(increment: number): void;
  seek(position: number): void;
  seekPause(): void;
  seekEnd(): void;
  seeking(): void;
  play(): void;
  pause(): void;
  ccAudio(): void;
  liveButton(): void;
  onControlHover(): void;
}

interface LiveControlsTypeConfig extends Lightning.Component.TypeConfig {
  SignalMapType: LiveControlsSignalMap;
}

const CONTROL_W = 1580;
const CONTROL_H = 264;

const CC_CONTAINER_H = 48;

const CONTROLS_CONTAINER_H = 73;
const CONTROLS_CONTAINER_Y = 42;

const FONT_SIZE = 29;

const LIVE_ICON_SIZE = 30;
const LIVE_ICON_ACTIVE = getImageTextureObj(
  'static/images/navigation/live-icon-active.svg',
  LIVE_ICON_SIZE,
  LIVE_ICON_SIZE,
);
const LIVE_ICON_INACTIVE = getImageTextureObj(
  'static/images/navigation/live-icon-inactive.svg',
  LIVE_ICON_SIZE,
  LIVE_ICON_SIZE,
);

const LIVE_BUTTON_H = 41;
const LIVE_BUTTON_W = 100;
const LIVE_BUTTON_MARGIN = 16;

const INACTIVE_ALPHA = 0.1;
const FOCUSED_ALPHA = 1;
const UNFOCUSED_ALPHA = 0.6;

export default class LiveControls
  extends Lightning.Component<LiveControlsTemplateSpec, LiveControlsTypeConfig>
  implements
    Lightning.Component.ImplementTemplateSpec<LiveControlsTemplateSpec>
{
  private _CcContainer = this.getByRef('CcContainer')!;
  private _CcButton = this._CcContainer.getByRef('CcButton')!;
  private _ProgressBar = this.getByRef('ProgressBar')!;
  private _ControlsContainer = this.getByRef('ControlsContainer')!;
  private _PlaybackControls =
    this._ControlsContainer.getByRef('PlaybackControls')!;
  private _SkipBackward = this._PlaybackControls.getByRef('SkipBackward')!;
  private _PlayPauseIcon = this._PlaybackControls.getByRef('PlayPauseIcon')!;
  private _SkipForward = this._PlaybackControls.getByRef('SkipForward')!;
  private _LiveButton = this._ControlsContainer.getByRef('LiveButton')!;

  private _savedState = 'PlayPause';
  private _hasDVR = false;
  private _isPlayingLive = true;
  private _isLiveStreamActive = true;
  private _isPlaying = true;

  private _liveBehindSeconds = 0;
  private _dvrWindowSize = 0;
  private _finalSeekPosition: number | null = null;
  private _liveSyncPosition: number | null = null;
  private _seekRate = DEFAULT_SEEK_RATE;
  private _seekSpeed = DEFAULT_SEEK_SPEED;

  private initialPlayPauseState:
    | keyof Pick<LiveControlsSignalMap, 'play' | 'pause'>
    | null = null;
  private scrubPosition: number | null = null;

  private finishScrubTimeout: null | (() => void) = null;

  static override _template(): Lightning.Component.Template<LiveControlsTemplateSpec> {
    return {
      w: CONTROL_W,
      h: CONTROL_H,
      flex: {
        direction: 'column',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
      },
      CcContainer: {
        w: CONTROL_W,
        h: CC_CONTAINER_H,
        flex: {
          justifyContent: 'flex-end',
        },
        CcButton: {
          ...CcAudioButton,
          signals: {
            $ccAudio: true,
            $onHover: '$onHover',
          },
        },
      },
      ProgressBar: {
        type: LiveProgressBar,
        signals: {
          $onCursorClick: '$onCursorClick',
          $onHover: '$onHover',
        },
      },
      ControlsContainer: {
        w: CONTROL_W,
        h: CONTROLS_CONTAINER_H,
        y: CONTROLS_CONTAINER_Y,
        PlaybackControls: {
          type: CommonPlaybackControls,
          x: CONTROL_W / 2,
          signals: {
            SkipBack: '$skipBackward',
            PlayPause: '$playPause',
            SkipForward: '$skipForward',
            onHover: '$onHover',
          },
        },
        LiveButton: {
          minWidth: LIVE_BUTTON_W,
          height: LIVE_BUTTON_H,
          margin: 0,
          paddingH: 0,
          x: CONTROL_W,
          mountX: 1,
          fontSize: FONT_SIZE,
          type: Button,
          action: '$liveButton',
          signals: {
            $liveButton: '$liveButton',
            $onHover: '$onHover',
          },
          backgroundColor: {
            focused: Colors('transparent').get(),
            unfocused: Colors('transparent').get(),
          },
          fontColor: {
            unfocused: Colors('appAccent').get(),
            focused: Colors('appAccent').get(),
          },
          label: translate('playback.live'),
          startIcon: {
            focused: {
              ...LIVE_ICON_ACTIVE,
              flexItem: { marginRight: LIVE_BUTTON_MARGIN },
            },
            unfocused: {
              ...LIVE_ICON_ACTIVE,
              flexItem: { marginRight: LIVE_BUTTON_MARGIN },
            },
          },
        },
      },
    };
  }

  set dvrWindowSize(seconds: number) {
    this._dvrWindowSize = seconds;
    this._ProgressBar.dvrWindowSize = seconds;
  }

  set liveSyncPosition(position: number | null) {
    this._liveSyncPosition = position;
  }

  set hasDVR(hasDVR: boolean) {
    this._hasDVR = hasDVR;
    this._PlaybackControls.visible = hasDVR;
    this._ProgressBar.hasDVR = hasDVR;
    this._ProgressBar.showProgressTime(hasDVR);
  }

  set isPlayingLive(isPlayingLive: boolean) {
    if (!this._hasDVR) return;
    // Disable Skip Forward button
    this._isPlayingLive = isPlayingLive;
    if (!this.isSeeking() && !this.scrubPosition) {
      this._ProgressBar.showProgressTime(!isPlayingLive);
    }
    this.updateLiveButton();
    this.enableSkipForward(!isPlayingLive);
  }

  set isLiveStreamActive(isLiveStreamActive: boolean) {
    if (!this._hasDVR) return;
    // Disable Skip Backward and Live buttons
    this._isLiveStreamActive = isLiveStreamActive;
    this.updateLiveButton();
  }

  set liveProgress(secondsBehindLiveEdge: number) {
    if (!this._hasDVR) return;
    this._liveBehindSeconds = secondsBehindLiveEdge;
    this._ProgressBar.progressTime =
      this.finalSeekPosition ?? secondsBehindLiveEdge;
  }

  set isPlayingStartOfHist(isPlayingStartOfHist: boolean) {
    if (!this._hasDVR) return;
    this.enableSkipBackward(!isPlayingStartOfHist);
  }

  get finalSeekPosition() {
    return this._finalSeekPosition;
  }

  set finalSeekPosition(finalSeekPosition: number | null) {
    this._finalSeekPosition = finalSeekPosition;
    if (finalSeekPosition === null) return;
    this._ProgressBar.progressTime = finalSeekPosition;
  }

  get seekRate() {
    return this._seekRate;
  }

  set seekRate(rate: number) {
    this._seekRate = rate;
  }

  get seekSpeed() {
    return this._seekSpeed;
  }

  set seekSpeed(speed: number) {
    this._ProgressBar.seekSpeed = this.seekRate > 0 ? speed : -speed;
    this._seekSpeed = speed;
  }

  override _focus() {
    if (this._hasDVR) this._setState(this._savedState);
    else this._setState('CcButton');
  }

  override _handleMediaPlay() {
    if (!this._hasDVR) return true;
    if (!this._isPlaying) this.finishSeeking('play');
    this._isPlaying = true;
    this.updatePlayPauseIcon();

    return false;
  }

  override _handleMediaPause() {
    if (!this._hasDVR) return true;
    if (this.isSeeking()) this.finishSeeking('pause');
    this._isPlaying = false;
    this.updatePlayPauseIcon();
    this._setState('PlayPause');

    return false;
  }

  preloadIcons() {
    this._PlaybackControls.preloadIcons();
    preloadTextures(this, [LIVE_ICON_ACTIVE, LIVE_ICON_INACTIVE]);
  }

  isSeeking() {
    return this._seekRate !== 1;
  }

  onRewind() {
    if (!this._hasDVR) return;
    this._setState('ProgressBar');
    if (this._isPlaying) this.signal('pause');
    this._PlaybackControls.onRewind();
  }

  onFastForward() {
    if (!this._hasDVR) return;
    this._setState('ProgressBar');
    if (this._isPlaying) this.signal('pause');
    this._PlaybackControls.onFastForward();
  }

  updateFromPause() {
    this._isPlaying = false;
    this.updatePlayPauseIcon();
  }

  updateFromPlay() {
    this._isPlaying = true;
    this.updatePlayPauseIcon();
  }

  private saveState() {
    this._savedState = this._getState();
  }

  private $onHover(target: Lightning.Component) {
    switch (target) {
      case this._CcButton:
        this._setState('CcButton');
        break;
      case this._ProgressBar.playbackProgressBar:
        if (!this._hasDVR) return;
        this._setState('ProgressBar');
        break;
      case this._SkipBackward:
        this.focusSkipBackward();
        break;
      case this._PlayPauseIcon:
        this._setState('PlayPause');
        break;
      case this._SkipForward:
        this.focusSkipForward();
        break;
      case this._LiveButton:
        this.focusLiveButton();
        break;
    }
    this.signal('onControlHover');
  }

  private $ccAudio() {
    this.signal('ccAudio');
  }

  private $skipBackward() {
    this.signal('skip', -SKIP_INTERVAL);
  }

  private $playPause() {
    this.signal(this._isPlaying ? 'pause' : 'play');
    this._isPlaying = !this._isPlaying;
    this.updatePlayPauseIcon();
  }

  private $skipForward() {
    this.signal('skip', SKIP_INTERVAL);
  }

  private $liveButton() {
    if (!this._hasDVR) return;
    this.signal('liveButton');
    if (!this._isPlaying) this.$playPause();
    this._setState('PlayPause');
  }

  private enableSkipBackward(enable: boolean) {
    this._SkipBackward.IconDisabled = !enable;
    if (!enable && this._getState() === 'SkipBackward') {
      this._setState('PlayPause');
    }
    if (enable) {
      this._SkipBackward.patch({ alpha: UNFOCUSED_ALPHA });
    }
  }

  private enableSkipForward(enable: boolean) {
    this._SkipForward.IconDisabled = !enable;
    if (!enable && this._getState() === 'SkipForward') {
      this._setState('PlayPause');
    }
    if (enable) {
      this._SkipForward.patch({ alpha: UNFOCUSED_ALPHA });
    }
  }

  private updateLiveButton() {
    if (!this._isLiveStreamActive) {
      this._LiveButton.patch({
        startIcon: {
          unfocused: { ...LIVE_ICON_INACTIVE, alpha: INACTIVE_ALPHA },
        },
        fontColor: {
          unfocused: Colors('text').alpha(INACTIVE_ALPHA).get(),
        },
      });
    } else {
      this._LiveButton.patch({
        startIcon: {
          focused: {
            ...(this._isPlayingLive ? LIVE_ICON_ACTIVE : LIVE_ICON_INACTIVE),
            alpha: FOCUSED_ALPHA,
          },
          unfocused: {
            ...(this._isPlayingLive ? LIVE_ICON_ACTIVE : LIVE_ICON_INACTIVE),
            alpha: this._isPlayingLive ? FOCUSED_ALPHA : UNFOCUSED_ALPHA,
          },
        },
        fontColor: {
          focused: this._isPlayingLive
            ? Colors('appAccent').get()
            : Colors('text').get(),
          unfocused: this._isPlayingLive
            ? Colors('appAccent').get()
            : Colors('text').alpha(UNFOCUSED_ALPHA).get(),
        },
      });
    }
  }

  private updatePlayPauseIcon() {
    this._PlayPauseIcon.patch({
      icon: this._isPlaying ? PAUSE_ICON : PLAY_ICON,
    });
  }

  private focusLiveButton() {
    if (!this._isPlayingLive && this._isLiveStreamActive && this._hasDVR) {
      this._setState('LiveButton');
    }
  }

  private focusSkipForward() {
    if (!this._SkipForward.IconDisabled && this._hasDVR) {
      this._setState('SkipForward');
    }
  }

  private focusSkipBackward() {
    if (!this._SkipBackward.IconDisabled && this._hasDVR) {
      this._setState('SkipBackward');
    }
  }

  private resetSeeking() {
    this.seekRate = DEFAULT_SEEK_RATE;
    this.seekSpeed = DEFAULT_SEEK_SPEED;
    this.finalSeekPosition = null;
    this.initialPlayPauseState = null;
    this.scrubPosition = null;

    this._PlaybackControls.clearSeekInterval();
    this._PlaybackControls.clearSeekAnnounceInterval();
  }

  private finishSeeking(forceState?: 'play' | 'pause') {
    if (this.finalSeekPosition === null || !this._liveSyncPosition) return;
    const newTarget =
      this.finalSeekPosition === 0
        ? Infinity
        : this._liveSyncPosition - this.finalSeekPosition;

    // Save position for current target
    this._liveBehindSeconds = this.finalSeekPosition;

    this.signal('seek', newTarget);

    if (forceState) {
      this.signal(forceState);
    } else if (this.initialPlayPauseState) {
      this.signal(this.initialPlayPauseState);
    }
    this.resetSeeking();
    this.signal('seekEnd');
  }

  private $onCursorClick(
    target: Lightning.Component,
    localCoords: LocalCoordsFromClick,
  ) {
    if (!this._hasDVR || target !== this._ProgressBar.playbackProgressBar)
      return;

    const targetPoint = localCoords.x / this._ProgressBar.playbackProgressBar.w;

    const secondsBehindLiveEdge = (1 - targetPoint) * this._dvrWindowSize;
    const seekTarget = Math.round(
      this._liveSyncPosition! - secondsBehindLiveEdge,
    );

    this.signal('onControlHover');
    this.signal('seek', seekTarget);
  }

  static override _states() {
    return [
      class ProgressBar extends this {
        private setupSeeking() {
          this._PlaybackControls.getSeekRate = () => this.seekRate;
          this._PlaybackControls.getSeekSpeed = () => this.seekSpeed;

          this._PlaybackControls.getInitialPlayPauseState = () => {
            if (!this.initialPlayPauseState) {
              this.initialPlayPauseState = this._isPlaying ? 'play' : 'pause';
            }
            this._PlaybackControls.clearSeekAnnounceInterval();
          };

          this._PlaybackControls.setRateSpeed = (newSeekRate, newSeekSpeed) => {
            this.seekRate = newSeekRate;
            this.seekSpeed = newSeekSpeed;
            this._PlaybackControls.setupAnnounceInterval(this);
          };

          this._PlaybackControls.finishSeeking = this.finishSeeking.bind(this);
          this._PlaybackControls.onIncrementSeek =
            this.onIncrementSeek.bind(this);
        }

        override $enter() {
          this.setupSeeking();
          this.saveState();

          this.finishScrubTimeout = debounce(
            this.finishSeeking.bind(this),
            SCRUB_DELAY,
          );
        }

        override $exit() {
          this.cleanup();
        }

        override _getFocused() {
          return this._ProgressBar;
        }

        override _inactive() {
          this.cleanup();
        }

        override _handleUp() {
          if (this.isSeeking()) this._handleEnter();
          this._setState('CcButton');
        }

        override _handleDown() {
          if (this.isSeeking()) this._handleEnter();
          this._setState('PlayPause');
        }

        override _handleBack() {
          if (!this.isSeeking()) return false;
          this._handleEnter();
        }

        override _handleEnter() {
          if (!this._isPlaying) {
            this.finishSeeking();
          }
          this.$playPause();
        }

        override _handleLeft() {
          this.scrub('backwards');
        }

        override _handleRight() {
          this.scrub('forwards');
        }

        private scrub(direction: 'forwards' | 'backwards') {
          if (!this.initialPlayPauseState) {
            this.initialPlayPauseState = this._isPlaying ? 'play' : 'pause';
          }
          this.signal('seekPause');

          this._ProgressBar.showProgressTime(true);

          const scrubIncrement =
            direction === 'forwards' ? -SCRUB_RATE : SCRUB_RATE;

          const nextScrubPosition =
            this.scrubPosition ?? this._liveBehindSeconds;
          this.scrubPosition = Math.min(
            this._dvrWindowSize,
            Math.max(0, nextScrubPosition + scrubIncrement),
          );

          this.finishScrubTimeout?.();
          this.finalSeekPosition = this.scrubPosition;
        }

        private onIncrementSeek() {
          this.signal('seeking');

          let lastPosition = this.finalSeekPosition ?? this._liveBehindSeconds;
          lastPosition -= this.seekRate;
          const newPosition = Math.min(
            this._dvrWindowSize,
            Math.max(0, lastPosition),
          );

          this.finalSeekPosition = newPosition;
          this.scrubPosition = newPosition;

          if (newPosition <= 0 || newPosition >= this._dvrWindowSize) {
            this.finishSeeking('play');
          }
        }

        private cleanup() {
          this.resetSeeking();
          this._ProgressBar.progressTime = this._liveBehindSeconds;
        }
      },
      class CcButton extends this {
        override $enter() {
          this.saveState();
        }

        override _getFocused() {
          return this._CcButton;
        }

        override _handleDown() {
          if (!this._hasDVR) return;
          this._setState('ProgressBar');
        }
      },

      class PlayPause extends this {
        override $enter() {
          this.saveState();
        }

        override _getFocused() {
          return this._PlayPauseIcon;
        }

        override _handleUp() {
          this._setState('ProgressBar');
        }

        override _handleLeft() {
          this.focusSkipBackward();
        }

        override _handleRight() {
          this.focusSkipForward();
        }
      },

      class SkipBackward extends this {
        override $enter() {
          this.saveState();
        }

        override _getFocused() {
          return this._SkipBackward;
        }

        override _handleUp() {
          this._setState('ProgressBar');
        }

        override _handleRight() {
          this._setState('PlayPause');
        }
      },

      class SkipForward extends this {
        override $enter() {
          this.saveState();
        }

        override _getFocused() {
          return this._SkipForward;
        }

        override _handleUp() {
          this._setState('ProgressBar');
        }

        override _handleLeft() {
          this._setState('PlayPause');
        }

        override _handleRight() {
          this.focusLiveButton();
        }
      },

      class LiveButton extends this {
        override $enter() {
          this.saveState();
        }

        override _getFocused() {
          return this._LiveButton;
        }

        override _handleUp() {
          this._setState('ProgressBar');
        }

        override _handleLeft() {
          if (this._SkipForward.IconDisabled) {
            this._setState('PlayPause');
          } else {
            this.focusSkipForward();
          }
        }
      },
    ];
  }
}
